'use client';

/**
 * AccessLevelMatrix Component
 *
 * Displays a matrix of permission groups × roles with 4-level access dropdowns.
 * Used for the Role Access Configuration page.
 *
 * Leverages existing design system components:
 * - GlassCard for container
 * - GlassChip for role badges and access level indicators
 * - GlassSelect for access level dropdowns
 * - RoleDefinition type from PermissionMatrix
 *
 * @see Role Access Configuration
 */

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  InputAdornment,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { GlassCard } from '../GlassCard/GlassCard';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';
import type { RoleDefinition } from '../PermissionMatrix';

// ============================================================================
// Types
// ============================================================================

export interface PermissionGroupInfo {
  /** Group key (e.g., 'PARTNER_MANAGEMENT') */
  key: string;
  /** Display name */
  name: string;
  /** Description */
  description?: string;
  /** Icon name */
  icon?: string;
  /** Number of permissions in this group */
  permissionCount: number;
}

export type AccessLevel = 'none' | 'view' | 'edit' | 'admin';

export interface AccessLevelMatrixProps {
  /** Permission groups to display as rows */
  permissionGroups: PermissionGroupInfo[];
  /** Roles to display as columns */
  roles: RoleDefinition[];
  /** Current access levels: roleId -> groupKey -> level */
  accessLevels: Record<string, Record<string, AccessLevel>>;
  /** Roles that cannot be edited (e.g., business_admin) */
  lockedRoles?: string[];
  /** Whether the matrix is editable */
  editable?: boolean;
  /** Handler when an access level changes */
  onAccessLevelChange?: (roleId: string, groupKey: string, newLevel: AccessLevel) => void;
  /** Default access levels for diff highlighting */
  defaultAccessLevels?: Record<string, Record<string, AccessLevel>>;
  /** Whether to show search */
  showSearch?: boolean;
  /** Loading state */
  isLoading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const ACCESS_LEVEL_CONFIG: Record<
  AccessLevel,
  { i18nKey: string; descKey: string; color: string; icon: React.ReactNode }
> = {
  none: {
    i18nKey: 'accessLevel.none',
    descKey: 'accessLevel.none.description',
    color: 'error.main',
    icon: <BlockIcon sx={{ fontSize: iconSizes.xs }} />,
  },
  view: {
    i18nKey: 'accessLevel.view',
    descKey: 'accessLevel.view.description',
    color: 'info.main',
    icon: <VisibilityIcon sx={{ fontSize: iconSizes.xs }} />,
  },
  edit: {
    i18nKey: 'accessLevel.edit',
    descKey: 'accessLevel.edit.description',
    color: 'warning.main',
    icon: <EditIcon sx={{ fontSize: iconSizes.xs }} />,
  },
  admin: {
    i18nKey: 'accessLevel.admin',
    descKey: 'accessLevel.admin.description',
    color: 'success.main',
    icon: <AdminPanelSettingsIcon sx={{ fontSize: iconSizes.xs }} />,
  },
};

const LEVELS: AccessLevel[] = ['none', 'view', 'edit', 'admin'];

// ============================================================================
// Sub-components
// ============================================================================

function AccessLevelDot({ level }: { level: AccessLevel }) {
  const config = ACCESS_LEVEL_CONFIG[level];
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: config.color,
        display: 'inline-block',
        mr: 1,
        flexShrink: 0,
      }}
    />
  );
}

function AccessLevelDropdown({
  value,
  onChange,
  disabled,
}: {
  value: AccessLevel;
  onChange: (level: AccessLevel) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value as AccessLevel);
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      disabled={disabled}
      size="small"
      sx={{
        minWidth: 130,
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.5,
          fontSize: '0.8125rem',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.divider,
        },
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      }}
      renderValue={(selected) => {
        const config = ACCESS_LEVEL_CONFIG[selected as AccessLevel];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessLevelDot level={selected as AccessLevel} />
            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
              {t(config.i18nKey)}
            </Typography>
          </Box>
        );
      }}
    >
      {LEVELS.map((level) => {
        const config = ACCESS_LEVEL_CONFIG[level];
        return (
          <MenuItem key={level} value={level} sx={{ gap: 1 }}>
            <AccessLevelDot level={level} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                {t(config.i18nKey)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              >
                {t(config.descKey)}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AccessLevelMatrix({
  permissionGroups,
  roles,
  accessLevels,
  lockedRoles = [],
  editable = false,
  onAccessLevelChange,
  defaultAccessLevels,
  showSearch = true,
  isLoading = false,
}: AccessLevelMatrixProps) {
  const { t } = useI18n();
  const theme = useTheme();
  const [search, setSearch] = useState('');

  const lockedRoleSet = useMemo(() => new Set(lockedRoles), [lockedRoles]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return permissionGroups;
    const q = search.toLowerCase();
    return permissionGroups.filter(
      (g) =>
        g.name.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q) || g.key.toLowerCase().includes(q),
    );
  }, [permissionGroups, search]);

  const isModified = (roleId: string, groupKey: string): boolean => {
    if (!defaultAccessLevels) return false;
    const current = accessLevels[roleId]?.[groupKey];
    const defaultLevel = defaultAccessLevels[roleId]?.[groupKey];
    return current !== defaultLevel;
  };

  if (isLoading) {
    return (
      <GlassCard>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('common.loading')}
          </Typography>
        </Box>
      </GlassCard>
    );
  }

  return (
    <GlassCard>
      <Box sx={{ p: 2 }}>
        {/* Search */}
        {showSearch && (
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder={t('roleAccess.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ maxWidth: 360 }}
            />
          </Box>
        )}

        {/* Matrix Table */}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    minWidth: 200,
                    bgcolor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle2">{t('customRoles.form.permissions')}</Typography>
                </TableCell>
                {roles.map((role) => (
                  <TableCell
                    key={role.id}
                    align="center"
                    sx={{
                      fontWeight: 600,
                      minWidth: 150,
                      bgcolor: theme.palette.background.default,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <GlassChip label={role.name} size="small" color={role.color || 'default'} variant="outlined" />
                      {lockedRoleSet.has(role.id) && (
                        <GlassTooltip title={t('roleAccess.lockedTooltip')}>
                          <LockIcon sx={{ fontSize: iconSizes['2xs'], color: 'text.disabled' }} />
                        </GlassTooltip>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGroups.map((group) => (
                <TableRow
                  key={group.key}
                  sx={{
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  {/* Group info */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {group.name}
                      </Typography>
                      {group.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                          }}
                        >
                          {group.description}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                        }}
                      >
                        {t('roleAccess.permissionCount', { count: group.permissionCount })}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Access level cells */}
                  {roles.map((role) => {
                    const isLocked = lockedRoleSet.has(role.id);
                    const level = accessLevels[role.id]?.[group.key] || 'none';
                    const modified = isModified(role.id, group.key);

                    return (
                      <TableCell key={role.id} align="center" sx={{ position: 'relative' }}>
                        {isLocked ? (
                          <GlassChip
                            label={t('accessLevel.admin')}
                            size="small"
                            color="success"
                            variant="filled"
                            icon={<LockIcon sx={{ fontSize: iconSizes.xs }} />}
                          />
                        ) : editable ? (
                          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <AccessLevelDropdown
                              value={level}
                              onChange={(newLevel) => onAccessLevelChange?.(role.id, group.key, newLevel)}
                            />
                            {modified && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -2,
                                  right: -2,
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'warning.main',
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <GlassChip
                            label={t(ACCESS_LEVEL_CONFIG[level].i18nKey)}
                            size="small"
                            color={
                              level === 'admin'
                                ? 'success'
                                : level === 'edit'
                                  ? 'warning'
                                  : level === 'view'
                                    ? 'info'
                                    : 'error'
                            }
                            variant="outlined"
                            icon={ACCESS_LEVEL_CONFIG[level].icon as React.ReactElement}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {filteredGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={roles.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('common.noResults')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Legend */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            {t('roleAccess.legend')}:
          </Typography>
          {LEVELS.map((level) => {
            const config = ACCESS_LEVEL_CONFIG[level];
            return (
              <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessLevelDot level={level} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t(config.i18nKey)} — {t(config.descKey)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </GlassCard>
  );
}
